import { useState, useEffect, useCallback } from "react"
import { Label } from "reactstrap"
import CreatableSelect from "react-select/creatable"
import { useVehicleOptions } from "hooks/stockHook"

const VehicleIdSelect = ({ formik}) => {
  const [inputValue, setInputValue] = useState("")
  const [debouncedValue, setDebouncedValue] = useState("")
  const [options, setOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)

  // 🔁 Debounce typing
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue.trim())
    }, 500)
    return () => clearTimeout(handler)
  }, [inputValue])

  const { data, isFetching } = useVehicleOptions(
    { identifier: debouncedValue },
    !!debouncedValue
  )

  // 🔄 Map API data to options
  useEffect(() => {
    if (data?.data?.length) {
      setOptions(
        data.data.map(item => ({
          label: `${item.stock_id} - ${item.full_chassis}`,
          value: item.id,
        }))
      )
    } else {
      setOptions([])
    }
  }, [data])

  // 🧠 Handle select
  const handleChange = option => {
    setSelectedOption(option)
    formik.setFieldValue("vehicle_id", option ? option.value : null)
  }

  // ➕ Handle create
  const handleCreate = input => {
    const created = {
      label: input,
      value: input, // string value for new entry
      __isNew__: true,
    }

    setOptions(prev => [...prev, created])
    setSelectedOption(created)
    formik.setFieldValue("vehicle_id", input)
  }

  // 🔁 Sync formik value (EDIT SAFE)
  useEffect(() => {
    const value = formik?.values?.vehicle_id

    if (!value) {
      setSelectedOption(null)
      return
    }

    // If value is object (when parent sets {label,value})
    if (typeof value === "object" && value?.value) {
      setSelectedOption(value)
      return
    }

    // If string (created manually)
    if (typeof value === "string") {
      setSelectedOption({
        label: value,
        value: value,
      })
    }
  }, [formik?.values?.vehicle_id, options])

  // 🎨 Styles (unchanged)
  const customStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "#fff",
      borderColor: state.isFocused ? "#80bdff" : "#d1d5db",
      boxShadow: state.isFocused
        ? "0 0 0 0.15rem rgba(0,123,255,0.25)"
        : "none",
    }),
    menu: base => ({
      ...base,
      zIndex: 10,
    }),
  }

  return (
    <>
      <Label>Vehicle ID</Label>

      <CreatableSelect
        value={selectedOption}
        options={options}
        isClearable
        isSearchable
        isLoading={isFetching}
        onChange={handleChange}
        onCreateOption={handleCreate}
        onInputChange={setInputValue}
        placeholder={isFetching ? "Loading..." : "Search or create Vehicle ID"}
        noOptionsMessage={() =>
          debouncedValue
            ? "No vehicle found — press Enter to create"
            : "Start typing to search"
        }
        styles={customStyles}
      />

      {formik?.touched?.vehicle_id && formik?.errors?.vehicle_id && (
        <div className="invalid-feedback d-block">
          {formik.errors.vehicle_id}
        </div>
      )}
    </>
  )
}

export default VehicleIdSelect
